import {RawHTMLWithFallback} from "@/app-autocomplete/components/RawHTMLWithFallback.tsx";
import {useCustomizedUrl} from "@/hooks/useCustomizedUrl.ts";
import {useTranslations} from "@/hooks/useTranslations.ts";
import {useScrollList} from "@/providers/ScrollListProvider.tsx";
import type {Hit as InstantsearchHit} from "instantsearch.js";
import type {SendEventForHits} from "instantsearch.js/es/lib/utils";
import {useEffect, useRef} from "react";

export default function ItemBox({hit, sendEvent}: {
    hit: InstantsearchHit<ItemboxAttributes>;
    sendEvent?: SendEventForHits,
}) {
    const tc = useTranslations();
    const {hasScrolledRef} = useScrollList();
    const customizedUrl = useCustomizedUrl();
    const itemRef = useRef<HTMLLIElement | null>(null);
    const itemLink = "/" + customizedUrl + hit.itemLink;
    const addToCartEvent = () => sendEvent && sendEvent("conversion", hit, "PLP: Product Added To Favorites");
    const addToFavoriteEvent = () => sendEvent && sendEvent("conversion", hit, "PLP: Product Added To Favorites");
    const showVariantButton = hit.hasVariants || hit.pConEnabled || hit.twoDConfiguratorEnabled;
    const variantButtonConfiguration = hit.pConEnabled || hit.twoDConfiguratorEnabled;
    useEffect(() => {
        const hash = decodeURIComponent(window.location.hash.replace("#", ""));
        if (!hasScrolledRef.current && hash === "item" + hit.objectID && itemRef.current) {
            itemRef.current.scrollIntoView({behavior: "smooth", block: "center"});
            hasScrolledRef.current = true;
        }
    }, [hasScrolledRef, hit.objectID]);
    return (
		<li
			ref={itemRef}
			key={hit.objectID}
			data-object-id={hit.objectID}
			id={"item_" + hit.itemId}
			className="ais-itemBox itembox"
			data-itemno={hit.itemNo}
			data-item-id={hit.itemId}
		>
			<a
				href={hit?.item_link ?? itemLink}
				title={hit.description}
				data-gtm-item={hit.itemId}
				className="itembox__main gtm-product-click"
				onClick={() => sendEvent && sendEvent("click", hit, "PLP: Product Clicked")}
				onAuxClick={() => sendEvent && sendEvent("click", hit, "PLP: Product Clicked")}
			>
				<div className="itembox__name">{hit.description}</div>
				<div className="itembox__variantsCount">
					{hit.numberOfVariants > 0 && (
						<>
							{hit.numberOfVariants} {hit.numberOfVariants === 1 ? tc.variant : tc.variants}
						</>
					)}
				</div>
				<div className="itembox__head">
					<div className="itembox__image">
						<img
							className="itembox__imageSrc"
							alt={`${tc.productImageOf} ${hit.description}`}
							src={hit?.itemImage /*? hit?.image_link : hit?.itemImage*/}
							width="344"
							height="255"
							loading="lazy"
						/>
					</div>
				</div>
			</a>
			<RawHTMLWithFallback className="itembox__warningTooltip" html={hit.warningTooltip} />
			<div className="itembox__footer">
				<div className="itembox__content">
					<div className="itembox__badges">
						<RawHTMLWithFallback className="campaignBadges" html={hit.campaigns} />
						<RawHTMLWithFallback className="promotions" html={hit.promotions} />
					</div>
					<RawHTMLWithFallback
						className="itembox__prices"
						html={hit.prices}
						fallback={
							<div className="prices loading">
								{hit.showFundamentalPrice && <div className={"basePrice loadSkeleton"}>Loading</div>}
								<div className={"fundamentalPrice loadSkeleton"}>Loading</div>
							</div>
						}
					/>
				</div>
				<div className="itembox__actions">
					<RawHTMLWithFallback
						onClick={() => addToFavoriteEvent()}
						onAuxClick={() => addToFavoriteEvent()}
						html={hit.favoriteSign}
						fallback={
							<span className="actionIcon">
								<i className="icon icon-favorites" aria-hidden="true" />
							</span>
						}
					/>
					{showVariantButton ? (
						<RawHTMLWithFallback
							html={hit.variantButton}
							fallback={
								<>
									{variantButtonConfiguration ? (
										<a href={hit?.item_link ?? itemLink} className="buttonIcon buttonIcon--primary buttonIcon--iconLarge">
											<i className="icon icon-settings" aria-hidden="true" />
										</a>
									) : (
										<a href={hit?.item_link ?? itemLink} className="buttonIcon buttonIcon--outline">
											<i className="icon icon-arrow-right" aria-hidden="true" />
										</a>
									)}
								</>
							}
						/>
					) : (
						<form
							name={"form_" + hit.itemId}
							id={"form_" + hit.itemId}
							method="POST"
							className="js-updateItem"
							data-action="shop_add_item_to_basket_list"
						>
							<input type="hidden" name="item_qty" value="1" />
							<input type="hidden" name="item_id" value={hit.itemId} />
							<RawHTMLWithFallback
								onClick={() => addToCartEvent()}
								onAuxClick={() => addToCartEvent()}
								html={hit.orderbutton}
								fallback={
									<div className={"orderButtonWrapper"} aria-hidden="true">
										<button type="submit" className="orderButton button buttonIcon--outline button--primary ">
											<i className="icon icon-basket" aria-hidden={true}></i>
										</button>
									</div>
								}
							/>
						</form>
					)}
				</div>
			</div>
			<RawHTMLWithFallback className="itembox__addon" html={hit.inventorySign} />
		</li>
	);
}
